import type { StructuredLogLine } from './logStreamClient';

// 级别列宽与后端 log_codec.render_line 的 !s:5 对齐，超长的级别名不截断
export const LEVEL_WIDTH = 5;

// 续行缩进，让堆栈与多行消息看得出从属关系
export const CONTINUATION_INDENT = '  ';

export type LogRowKind = 'head' | 'continuation' | 'raw';

export interface LogDisplayRow {
  kind: LogRowKind;
  // 级别原样带出，着色交给 CSS 类，不在渲染热路径上拼解转义序列
  level: string;
  ts: string;
  instances: string;
  text: string;
}

function splitLines(text: string): string[] {
  return text.split('\n').map(line => (line.endsWith('\r') ? line.slice(0, -1) : line));
}

/** 级别的定宽单元格，形如 [ERROR] */
export function formatLevelCell(level: string): string {
  return `[${level.padEnd(LEVEL_WIDTH)}]`;
}

/** 一条展示行的纯文本形态，面板渲染与导出共用，免得两处格式各写一份慢慢跑偏 */
export function displayRowToPlainText(row: LogDisplayRow): string {
  if (row.kind === 'head') {
    return `[${row.ts}] ${formatLevelCell(row.level)} [${row.instances}] ${row.text}`;
  }
  if (row.kind === 'continuation') {
    return `${CONTINUATION_INDENT}${row.text}`;
  }
  return row.text;
}

/**
 * 一行结构化日志摊平成若干条展示行。
 *
 * 注意，一行一条文本是窗口化列表的前提，条目内嵌换行会让固定行高不再成立。
 */
export function renderStructuredLogLine(line: StructuredLogLine): LogDisplayRow[] {
  const level = String(line.level ?? '');
  const message = String(line.message ?? '');

  // file 源把解析不了的残留行包成 PRINT 级透传，比如 print 输出，这类行不带时间戳与级别，原样呈现
  if (level === 'PRINT') {
    return splitLines(message).map(text => ({ kind: 'raw' as const, level, ts: '', instances: '', text }));
  }

  const ts = String(line.ts ?? '');
  const instances = String(line.instances ?? '');
  const [firstLine, ...continuation] = splitLines(message);
  const rows: LogDisplayRow[] = [{ kind: 'head', level, ts, instances, text: firstLine }];
  continuation.forEach(text => {
    rows.push({ kind: 'continuation', level, ts: '', instances: '', text });
  });
  if (line.stack) {
    splitLines(String(line.stack)).forEach(text => {
      rows.push({ kind: 'continuation', level, ts: '', instances: '', text });
    });
  }
  return rows;
}

/** 一批结构化行摊平成展示行数组 */
export function renderStructuredLogLines(lines: StructuredLogLine[]): LogDisplayRow[] {
  const rows: LogDisplayRow[] = [];
  lines.forEach(line => {
    renderStructuredLogLine(line).forEach(row => rows.push(row));
  });
  return rows;
}

/** 展示行数组的纯文本形态，导出文件用，行序与面板一致 */
export function displayRowsToText(rows: LogDisplayRow[]): string {
  return rows.map(displayRowToPlainText).join('\n');
}
